import { DISABLE_LIST_QUERY_OPTIONS } from 'constants/disableListQueryOptions';
import {
  EXPORT_TYPE_NAMES,
  ExportTypes,
  ExportJobTypes,
  ExportTransportTypes,
  ReportNames,
} from 'constants/exportTypes';
import { ExportType } from 'constants/exportTypes';
import { Resources } from 'constants/resources';

import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'config/requests';
import { useReportExport } from 'hooks/queries/useReportExport';
import { useGetCumulativeReport } from 'pages/Finance/CumulativeReport/hooks/useGetCumulativeReport';
import {
  ListContextProvider,
  ListControllerResult,
  useListController,
  useNotify,
} from 'react-admin';
import { ExportTypesButton } from 'shared/mui/Buttons/ExportTypesButton/ExportTypesButton';
import { PageHeader } from 'shared/mui/PageHeader';
import { Typography } from 'shared/mui/Typography';
import { ExportEditButton } from 'shared/react-admin/Export/ExportEditButton/ExportEditButton';
import { ReportExportDialog } from 'shared/widgets/ReportExportDialog/ReportExportDialog';
import { CumulativeReport } from 'types/embedded/cumulativeReport';

import { EXPORT_FIELDS_CUMULATIVE_REPORT } from './constants/exportFields';
import { CumulativeReportListContent } from './CumulativeReportListContent';

export const CumulativeReportList: React.FC = () => {
  const [exportType, setExportType] = useState<ExportType>(ExportTypes.Excel);
  const [isSendReportOnEmail, setIsSendReportOnEmail] = useState(false);
  const [isOpenNotifyDialog, setIsOpenNotifyDialog] = useState(false);
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: getUserInfo });
  const notify = useNotify();

  const context = useListController<CumulativeReport>({
    resource: `v2/${Resources.CumulativeReport}`,
    queryOptions: DISABLE_LIST_QUERY_OPTIONS,
  });

  const { cumulativeReport, isLoading } = useGetCumulativeReport(context.filterValues);

  const { mutate: exportReportMutate, isLoading: isExportLoading } = useReportExport({
    filters: {
      ...context.filterValues,
      exportType,
      jobType: ExportJobTypes.Cumulative,
      ...(isSendReportOnEmail && { notificationTransport: [ExportTransportTypes.Email] }),
    },
    preferenceKey: ReportNames.Cumulative,
    onSuccess: () => {
      notify(
        `Файл экспорта отчёта по нарастающему итогу формируется ${isSendReportOnEmail ? 'и будет отправлен вам на почту' : ''}`,
        {
          type: 'info',
        },
      );
      setIsOpenNotifyDialog(false);
    },
    onError: (error) => {
      notify(error.data?.errors[0]?.title, { type: 'error' });
    },
  });

  return (
    <ListContextProvider
      value={
        {
          ...context,
          data: cumulativeReport?.reportItems || [],
        } as ListControllerResult
      }
    >
      <PageHeader
        actions={[
          <ExportTypesButton
            disabled={!Object.keys(context.filterValues).length}
            exportType={exportType}
            key="export-button"
            onChangeType={setExportType}
            onClick={() => setIsOpenNotifyDialog(true)}
          />,
        ]}
        headerTitle={{
          titleText: 'Отчёт по нарастающему итогу',
          tooltipText:
            'Нарастающий итог – это сумма показателей, где к данным текущего периода добавляются суммы предыдущих периодов.',
        }}
        mt={3}
      />
      <CumulativeReportListContent cumulativeReport={cumulativeReport} isLoading={isLoading} />
      <ReportExportDialog
        exportType={EXPORT_TYPE_NAMES[exportType]}
        isLoading={isExportLoading}
        onChange={() => setIsSendReportOnEmail((prev) => !prev)}
        onClose={() => setIsOpenNotifyDialog(false)}
        onSubmit={exportReportMutate}
        open={isOpenNotifyDialog}
        sendOnEmail={isSendReportOnEmail}
        title={
          <>
            <Typography component="span" sx={{ verticalAlign: 'middle' }} variant="inherit">
              Экспорт отчёта
            </Typography>
            <ExportEditButton
              fields={EXPORT_FIELDS_CUMULATIVE_REPORT}
              preferenceKey={ReportNames.Cumulative}
            />
          </>
        }
        userEmail={user?.corporateEmail || user?.email}
      />
    </ListContextProvider>
  );
};
